/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";

// react-router-dom components
import { useLocation, NavLink, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";

// Material Dashboard 2 React components
import MDBox from "../../components/MDBox";
import MDTypography from "../../components/MDTypography";
import MDButton from "../../components/MDButton";

// Material Dashboard 2 React example components
import SidenavCollapse from "./SidenavCollapse";

// Custom styles for the Sidenav
import SidenavRoot from "./SidenavRoot";
import sidenavLogoLabel from "./styles/sidenav";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "../../context/contexts";
import { Close, Logout } from "@mui/icons-material";
import { tryLogout } from "../../stateManagement/reducers/authReducer";
import { useDispatch, useSelector } from "react-redux";
import PopUp from "../../components/PopUp/PopUp";
import { Backdrop, Box, Card, CircularProgress, Grid, Grow, IconButton, Stack, Typography, useTheme } from "@mui/material";
import AnimateButton from "../../components/@extended/AnimateButton";
import { CUSTOMER, DIAMOND, GOLD, MEMBERSHIP_TYPES, PLATINUM, SILVER } from "../../config/constants";
import { imitateCustomer, tryImitateCustomer } from "../../stateManagement/reducers/imitationsReducer";

const LogoutButton = () => {

  const {
    authToken,
    loggingOut,
    loggedOut,
    logoutErr
  } = useSelector(state => state.auth);

  const dispatch = useDispatch();

  return (
    <Stack
      alignItems="center"
      sx={{
        width: '100%'
      }}
    >
      <MDBox
        mt="auto"
        sx={{
          p: '0!important',
          width: 'calc( 100% - 2rem) !important'
        }}
      >
        <AnimateButton>
          <MDButton
            component="a"
            rel="noreferrer"
            variant="gradient"
            color="error"
            fullWidth
            onClick={e => {
              dispatch(tryLogout({ authToken: authToken ?? "" }));
            }}
            endIcon={<Logout />}
          >
            Logout
          </MDButton>
        </AnimateButton>
      </MDBox>
    </Stack>
  );
}

const HomeButton = ({ handleClick }) => {

  const navigate = useNavigate();

  return (
    <>
      <Stack
        alignItems="center"
        sx={{
          width: '100%'
        }}
      >
        <MDBox
          mt="auto"
          sx={{
            p: '0!important',
            width: 'calc( 100% - 2rem) !important'
          }}
        >
          <AnimateButton>
            <MDButton
              component="a"
              rel="noreferrer"
              variant="gradient"
              color="primary"
              fullWidth
              onClick={e => {
                // navigate('/welcome');
                handleClick();
              }}
              endIcon={<Logout />}
            >
              Mimic Customer
            </MDButton>
          </AnimateButton>
        </MDBox>
      </Stack>
    </>
  );
}


function Sidenav({ color, brand, brandName, routes, ...rest }) {

  const {
    authToken,
    loggingOut,
    loggedOut,
    logoutErr
  } = useSelector(state => state.auth);

  const [controller, dispatch] = useMaterialUIController();
  const closeSidenav = () => setMiniSidenav(dispatch, true);
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, sidenavColor } = controller;
  const location = useLocation();
  const collapseName = location.pathname.replace("/", "");
  const [showMembershipOptions, setShowMembershipOptions] = useState(false);
  const theme = useTheme();
  const reduxDispatch = useDispatch();
  const { user } = useSelector(state => state.auth);
  const [customerRole, setCustomerRole] = useState(null);

  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  let itr = 0;

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      // setMiniSidenav(dispatch, window.innerWidth < 1200);
      // setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      // setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(({ type, name, icon, title, noCollapse, key, href, route }) => {
    let returnValue;

    if (type === "collapse") {
      returnValue = href ? (
        <Link
          href={href}
          key={key} left
          target="_blank"
          rel="noreferrer"
          sx={{ textDecoration: "none" }}
          onClick={closeSidenav}
        >
          <SidenavCollapse
            name={name}
            icon={icon}
            active={key === collapseName}
            noCollapse={noCollapse}
          />
        </Link>
      ) : (
        <NavLink key={key} to={route}>
          <SidenavCollapse onClick={() => closeSidenav()} name={name} icon={icon} active={key === collapseName} />
        </NavLink>
      );
    } else if (type === "title") {
      returnValue = (
        <MDTypography
          key={key}
          color={textColor}
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          pl={3}
          mt={2}
          mb={1}
          ml={1}
        >
          {title}
        </MDTypography>
      );
    } else if (type === "divider") {
      returnValue = (
        <Divider
          key={key}
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
        />
      );
    }

    return returnValue;
  });

  return (
    <>
      <SidenavRoot
        {...rest}
        variant="permanent"
        ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
        anchor="right"
        sx={{

        }}
        hideBackdrop
      >
        <MDBox sx={{ flex: '0 !important' }} pt={3} pb={1} px={4} textAlign="center">
          <MDBox
            display={{ xs: "block", xl: "none" }}
            position="absolute"
            top={0}
            right={0}
            p={1.625}
            onClick={closeSidenav}
            sx={{ cursor: "pointer" }}
          >
            <MDTypography variant="h6" color="secondary">
              <Close />
            </MDTypography>
          </MDBox>
          <MDBox component={NavLink} to="/" display="flex" alignItems="center">
            {brand && <MDBox component="img" src={brand} alt="Brand" width="2rem" />}
            <MDBox
              width={!brandName && "100%"}
              sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
            >
              <MDTypography component="h6" variant="button" fontWeight="medium" color={textColor}>
                {brandName}
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
        <Divider
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
        />
        <List
          sx={{
            flex: '1 !important',
            overflow: 'auto'
          }}
        >{renderRoutes}</List>
        <Stack
          spacing={1}
          sx={{
            width: '100%',
            height: 'fit-content',
            mt: 'auto !important',
            mb: '1rem',
            flex: '0 !important'
          }}
        >
          <HomeButton handleClick={() => {
            closeSidenav();
            setShowMembershipOptions(true);
          }} />
          <LogoutButton />
        </Stack>
      </SidenavRoot>
      {
        (loggingOut)

        &&

        <PopUp id='reset-password-popup' notCloseable deployed={loggingOut} setDeployed={(truth) => { }} >
          <MDBox>
            <CircularProgress color='error' />
          </MDBox>
        </PopUp>
      }
      {/* {miniSidenav && ( */}
      <Backdrop
        open={!miniSidenav}
        onClick={closeSidenav}
        sx={{ zIndex: (theme) => theme.zIndex.drawer - 1 }}
      />
      <Backdrop
        sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
        open={showMembershipOptions}
        onClick={() => {
          setShowMembershipOptions(false);
        }}

      >
        <Stack
          alignItems="center"
          spacing={2}
          sx={{
            width: 'fit-content',
            height: 'fit-content'
          }}
        >
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              fontSize: '1.25rem !important',
              lineHeight: '1.25rem !important',
              fontWeight: '700',
              color: 'white'
            }}
          >Select a Customer View</Typography>
          <Grid
            container
            justifyContent="stretch"
            spacing={2}
            sx={{
              width: '400px',
              height: '400px',
              mt: '-.5rem !important'
            }}
          >
            {
              MEMBERSHIP_TYPES.map((membershipType, i) => {

                let background = 'white !important';

                switch (membershipType) {
                  case SILVER:
                    background = 'linear-gradient(100deg, #999 9.86%, #D9D9D9 63.31%, #737373 93.6%) !important';
                    break;
                  case GOLD:
                    background = 'linear-gradient(100deg, #B28B04 9.86%, #FFFA89 51.73%, #946409 93.6%) !important';
                    break;
                  case PLATINUM:
                    background = 'linear-gradient(100deg, #E3C2EB 9.86%, #F7EDFF 63.31%, #9D86A8 93.6%) !important'
                    break;
                  case DIAMOND:
                    background = 'linear-gradient(100deg, #DEFFFD 9.86%, #FCFFFF 63.31%, #DCFFFD 93.6%) !important'
                    break;
                  default:
                    break;
                }


                return (
                  <Grid
                    key={i}
                    item
                    xs={6}
                    sx={{
                      '& > *': {
                        width: '100%',
                        height: '100%'
                      }
                    }}
                  >
                    <AnimateButton>
                      <Grow
                        in={showMembershipOptions}
                        style={{ transformOrigin: '0 0 0' }}
                        {...(showMembershipOptions ? { timeout: i * 1000 } : {})}
                      >
                        <Card
                          sx={{
                            width: '100%',
                            height: '100%',
                            borderRadius: '.25rem',
                            boxShadow: theme.shadows[10],
                            background,
                            cursor: 'pointer'
                          }}
                          onClick={() => {
                            if (user) {
                              reduxDispatch(tryImitateCustomer({
                                customerRole: { _id: 'dummy', name: CUSTOMER },
                                user,
                                membershipType
                              }));
                            }
                          }}
                        >
                          <Stack
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                              width: '100%',
                              height: '100%'
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{
                                textAlign: "center",
                                fontSize: '1.25rem !important',
                                lineHeight: '1.25rem !important',
                                fontWeight: '700',
                                color: 'black'
                              }}
                            >{membershipType.toUpperCase()}</Typography>
                          </Stack>
                        </Card>
                      </Grow>
                    </AnimateButton>
                  </Grid>
                )
              })
            }
          </Grid>
          <Stack
            alignItems="center"
            sx={{
              width: '100%',
              '& > *': {
                width: '100% !important',
                pl: '1rem !important'
              }
            }}
          >
            <AnimateButton>
              <Grow
                in={showMembershipOptions}
                style={{ transformOrigin: '0 0 0' }}
                {...(showMembershipOptions ? { timeout: 4 * 1000 } : {})}
              >
                <Card
                  sx={{
                    width: '100%',
                    height: 'fit-content',
                    p: 2,
                    borderRadius: '.25rem',
                    boxShadow: theme.shadows[10],
                    backgroundColor: 'black !important',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    if (user) {
                      reduxDispatch(tryImitateCustomer({
                        customerRole: { _id: 'dummy', name: CUSTOMER },
                        user,
                      }));
                    }
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      width: '100%',
                      textAlign: "center",
                      fontSize: '1.25rem !important',
                      lineHeight: '1.25rem !important',
                      fontWeight: '700',
                      color: 'white  !important'
                    }}
                  >Regular Customer</Typography>
                </Card>
              </Grow>
            </AnimateButton>
          </Stack>
        </Stack>
      </Backdrop>
      {/* )} */}
    </>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
